import React, { useEffect, useState } from "react";
import { useSiteContext } from "../SiteContext";
import { getEmployeeTimesheets, generateAuditReport, generateTimePunchReport } from "../api/audit";
import { Timesheet } from "../interfaces/AuditInterface";
import { getDateString } from "../util/DateAndTimeUtil";

interface AuditReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeID: number;
  altEmployeeID: string;
  employeeName: string;
}

const AuditReportModal: React.FC<AuditReportModalProps> = ({
  isOpen,
  onClose,
  employeeID,
  altEmployeeID,
  employeeName
}) => {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [selectedTimesheets, setSelectedTimesheets] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const { setLoading, createPopup } = useSiteContext();

  useEffect(() => {
    const fetchTimesheets = async () => {
      if (!isOpen || !employeeID) {
        setHasLoaded(false);
        return;
      }

      setIsLoading(true);
      setLoading(true);
      setHasLoaded(false);
      try {
        const response = await getEmployeeTimesheets(employeeID);
        
        // Sort timesheets by PayPeriodID descending
        const sorted = response.timesheets.sort((a, b) => {
          return b.payPeriod.payPeriodID - a.payPeriod.payPeriodID;
        });
        
        setTimesheets(sorted);
        
        // Select all by default
        const allIDs = new Set(sorted.map((ts) => ts.timesheetID));
        setSelectedTimesheets(allIDs);
        setSelectAll(true);
        setHasLoaded(true);
      } catch (error) {
        console.error("Failed to fetch timesheets", error);
        setTimesheets([]);
        setSelectedTimesheets(new Set());
        setSelectAll(false);
        setHasLoaded(true);
      } finally {
        setIsLoading(false);
        setLoading(false);
      }
    };

    fetchTimesheets();
  }, [isOpen, employeeID, setLoading]);

  const handleCheckboxChange = (timesheetID: number) => {
    setSelectedTimesheets((prev) => {
      const updated = new Set(prev);
      if (updated.has(timesheetID)) {
        updated.delete(timesheetID);
      } else {
        updated.add(timesheetID);
      }
      setSelectAll(updated.size === timesheets.length);
      return updated;
    });
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedTimesheets(new Set());
    } else {
      const allIDs = new Set(timesheets.map((ts) => ts.timesheetID));
      setSelectedTimesheets(allIDs);
    }
    setSelectAll(!selectAll);
  };

  const handleGenerateReport = async () => {
    if (selectedTimesheets.size === 0) {
      createPopup("Error", "Please select at least one timesheet to generate the audit report.");
      return;
    }

    setLoading(true);
    try {
      const timesheetIDs = Array.from(selectedTimesheets);
      const success = await generateAuditReport(employeeID, timesheetIDs, altEmployeeID);
      
      if (success) {
        createPopup("Success", "Audit report generated and downloaded successfully.");
        onClose();
      }
    } catch (error) {
      console.error("Error generating audit report:", error);
      createPopup("Error", "Failed to generate audit report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTimePunchReport = async () => {
    if (selectedTimesheets.size === 0) {
      createPopup("Error", "Please select at least one timesheet to generate the audit time punch report.");
      return;
    }

    setLoading(true);
    try {
      const timesheetIDs = Array.from(selectedTimesheets);
      const success = await generateTimePunchReport(employeeID, timesheetIDs, altEmployeeID);
      
      if (success) {
        createPopup("Success", "Audit time punch report generated and downloaded successfully.");
        onClose();
      }
    } catch (error) {
      console.error("Error generating audit time punch report:", error);
      createPopup("Error", "Failed to generate audit time punch report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !hasLoaded) return null;

  return (
    <>
      <style dangerouslySetInnerHTML={{__html: `
        .batch-export-modal {
          width: 650px !important;
        }
      `}} />
      <div className="batch-export-modal-overlay" onClick={onClose}>
        <div className="batch-export-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="batch-export-title">
          Timesheets for {employeeName} - {altEmployeeID}
        </h2>

        {timesheets.length === 0 ? (
          <p className="batch-export-no-employees">No timesheets found for this employee.</p>
        ) : (
          <>
            <div className="batch-export-select-all">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAllChange}
                id="audit-report-select-all"
              />
              <label htmlFor="audit-report-select-all">Select All</label>
            </div>

            <ul className="batch-export-employee-list">
              {timesheets.map((timesheet) => (
                <li key={timesheet.timesheetID} className="batch-export-employee-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedTimesheets.has(timesheet.timesheetID)}
                      onChange={() => handleCheckboxChange(timesheet.timesheetID)}
                    />
                    <span style={{ marginLeft: '20px' }}>
                      Week {timesheet.payPeriod.weekNumber}, {timesheet.payPeriod.accountingYear} {getDateString(timesheet.payPeriod.startDate)} - {getDateString(timesheet.payPeriod.endDate)}
                    </span>
                  </label>
                </li>
              ))}
            </ul>

            <div className="batch-export-modal-footer">
              <button 
                className="batch-export-button" 
                onClick={handleGenerateReport}
                disabled={selectedTimesheets.size === 0}
              >
                Generate Audit Report
              </button>
              <button 
                className="batch-export-button" 
                onClick={handleGenerateTimePunchReport}
                disabled={selectedTimesheets.size === 0}
              >
                Audit Time Punch Report
              </button>
              <button className="batch-export-cancel" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}
        </div>
      </div>
    </>
  );
};

export default AuditReportModal;
